package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.model.AppError
import br.com.vvrefeicoes.portal.core.model.AppResult
import br.com.vvrefeicoes.portal.core.storage.KeyValueStorage
import br.com.vvrefeicoes.portal.data.remote.ForgotPasswordRequestDto
import br.com.vvrefeicoes.portal.data.remote.LoginRequestDto
import br.com.vvrefeicoes.portal.data.remote.LoginResponseDto
import br.com.vvrefeicoes.portal.data.remote.RefreshRequestDto
import br.com.vvrefeicoes.portal.data.remote.ResetPasswordRequestDto
import br.com.vvrefeicoes.portal.data.remote.VerifyResetCodeRequestDto
import br.com.vvrefeicoes.portal.data.remote.VerifyResetCodeResponseDto
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.Session
import br.com.vvrefeicoes.portal.domain.model.UserRole
import br.com.vvrefeicoes.portal.domain.model.toUnitId
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * O refresh token do login fica salvo em [storage] (localStorage na web) para a sessão sobreviver ao F5.
 * O access token fica só em memória e é renovado por `POST /auth/refresh` quando o app abre.
 */
class RemoteAuthRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val storage: KeyValueStorage,
) : AuthRepository {
    private val _session = MutableStateFlow<Session?>(null)
    override val session: StateFlow<Session?> = _session.asStateFlow()

    override suspend fun login(
        login: String,
        password: String,
    ): AppResult<Session> {
        val response =
            try {
                httpClient.post("$baseUrl/auth/login") {
                    contentType(ContentType.Application.Json)
                    setBody(LoginRequestDto(email = login.trim(), password = password))
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                return AppResult.Failure(AppError.Network)
            }

        return when {
            response.status.isSuccess() -> toSession(response)
            // 400 só acontece com campo vazio; para o usuário é o mesmo que credencial inválida.
            response.status == HttpStatusCode.Unauthorized || response.status == HttpStatusCode.BadRequest ->
                AppResult.Failure(AppError.Unauthorized)
            else -> AppResult.Failure(AppError.Unexpected("HTTP ${response.status.value}"))
        }
    }

    override suspend fun restoreSession() {
        val refreshToken = storage.getString(REFRESH_TOKEN_KEY) ?: return
        val response =
            try {
                httpClient.post("$baseUrl/auth/refresh") {
                    contentType(ContentType.Application.Json)
                    setBody(RefreshRequestDto(refreshToken = refreshToken))
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                // Sem conexão: mantém o token salvo para tentar de novo no próximo carregamento.
                return
            }

        when {
            response.status.isSuccess() -> toSession(response)
            // Token expirado, inválido ou usuário inativo: não adianta tentar de novo.
            response.status == HttpStatusCode.Unauthorized || response.status == HttpStatusCode.BadRequest -> {
                _session.value = null
                storage.remove(REFRESH_TOKEN_KEY)
            }
        }
    }

    private suspend fun toSession(response: HttpResponse): AppResult<Session> {
        val body =
            try {
                response.body<LoginResponseDto>()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                return AppResult.Failure(AppError.Unexpected(throwable.message, throwable))
            }
        // A sessão carrega autorização (permissões funcionais, escopo de unidades e perfil) para o gating
        // do menu e dos seletores de unidade. Campos ausentes caem nos defaults do modelo.
        val user = body.user
        val session =
            Session(
                token = body.token,
                userName = user.nome,
                fullName = user.nome,
                role = parseRole(user.perfilAcesso),
                companies = user.companies.map { CompanyRef(id = it.id.toUnitId(), name = it.name) },
                permissions =
                    body.permissions.ifEmpty {
                        user.permissions.mapValues { (_, actions) -> actions.mapNotNull(::parsePermissionAction).toSet() }
                    },
            )
        // O /auth/refresh devolve um refresh token novo; guarda sempre o mais recente.
        storage.putString(REFRESH_TOKEN_KEY, body.refreshToken)
        _session.value = session
        return AppResult.Success(session)
    }

    override suspend fun logout() {
        storage.remove(REFRESH_TOKEN_KEY)
        _session.value = null
    }

    // A API responde 202 mesmo para e-mail não cadastrado, para não revelar quem tem conta.
    override suspend fun requestToken(email: String): AppResult<Boolean> {
        val response =
            postJson("/auth/password/forgot", ForgotPasswordRequestDto(email = email.trim()))
                ?: return AppResult.Failure(AppError.Network)
        return if (response.status.isSuccess()) AppResult.Success(true) else unexpected(response)
    }

    override suspend fun verifyResetCode(
        email: String,
        code: String,
    ): AppResult<String> {
        val response =
            postJson("/auth/password/verify", VerifyResetCodeRequestDto(email = email.trim(), code = code))
                ?: return AppResult.Failure(AppError.Network)
        return when {
            response.status.isSuccess() ->
                try {
                    AppResult.Success(response.body<VerifyResetCodeResponseDto>().resetToken)
                } catch (exception: CancellationException) {
                    throw exception
                } catch (exception: Exception) {
                    AppResult.Failure(AppError.Unexpected(exception.message, exception))
                }
            // 400 é código errado ou sem pedido pendente para o e-mail.
            response.status == HttpStatusCode.BadRequest -> AppResult.Failure(AppError.Unauthorized)
            response.status == HttpStatusCode.Gone -> AppResult.Failure(AppError.Expired)
            response.status == HttpStatusCode.TooManyRequests -> AppResult.Failure(AppError.TooManyAttempts)
            else -> unexpected(response)
        }
    }

    override suspend fun resetPassword(
        resetToken: String,
        newPassword: String,
    ): AppResult<Unit> {
        val response =
            postJson(
                "/auth/password/reset",
                ResetPasswordRequestDto(resetToken = resetToken, newPassword = newPassword, confirmPassword = newPassword),
            ) ?: return AppResult.Failure(AppError.Network)
        return when {
            response.status.isSuccess() -> AppResult.Success(Unit)
            response.status == HttpStatusCode.Unauthorized -> AppResult.Failure(AppError.Unauthorized)
            response.status == HttpStatusCode.Gone -> AppResult.Failure(AppError.Expired)
            else -> unexpected(response)
        }
    }

    /** Devolve nulo quando não foi possível falar com o servidor. */
    private suspend inline fun <reified T> postJson(
        path: String,
        body: T,
    ): HttpResponse? =
        try {
            httpClient.post("$baseUrl$path") {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Exception) {
            null
        }

    private fun unexpected(response: HttpResponse): AppResult.Failure = AppResult.Failure(AppError.Unexpected("HTTP ${response.status.value}"))

    private companion object {
        const val REFRESH_TOKEN_KEY = "auth.refreshToken"
    }
}

private fun parsePermissionAction(raw: String): PermissionAction? = PermissionAction.entries.firstOrNull { it.name.equals(raw.trim(), ignoreCase = true) }

private fun parseRole(raw: String?): UserRole? {
    val value = raw?.trim()?.lowercase() ?: return null
    return when {
        "diretor" in value -> UserRole.DIRECTOR
        "gerente" in value || "gestor" in value -> UserRole.MANAGER
        "supervisor" in value -> UserRole.SUPERVISOR
        else -> null
    }
}
